import React, { useCallback, useEffect, useRef, useState } from "react";

import { useReducedMotion } from "@ui/hooks/use-reduced-motion";

import * as styles from "./AnimatedNumber.css";

// Helper function to clear all animation classes from an element
function clearAnimationClasses(element: HTMLElement) {
  element.className = element.className
    .replace(styles.exitAnimation, "")
    .replace(styles.exitAnimationDown, "")
    .replace(styles.enterAnimation, "")
    .replace(styles.enterAnimationDown, "")
    .replace(styles.enterAnimationDownFromTop, "");
}

// Shared hook for animation logic
function useAnimatedNumber(number: number, formatted: boolean = false) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const isInitialRenderRef = useRef(true);
  const previousNumberRef = useRef<number>(number);
  const displayNumberRef = useRef<number>(number);
  const isAnimatingRef = useRef<boolean>(false);
  const [displayNumber, setDisplayNumber] = useState(number);
  const [isAnimating, setIsAnimating] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  // Keep refs in sync with state
  displayNumberRef.current = displayNumber;
  isAnimatingRef.current = isAnimating;

  const animateNumber = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    // Skip animations if user prefers reduced motion
    if (prefersReducedMotion) {
      previousNumberRef.current = number;
      displayNumberRef.current = number;
      setDisplayNumber(number);
      return;
    }

    // Skip animation on first render - let the CSS handle the initial slide-in
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      previousNumberRef.current = number;
      displayNumberRef.current = number;
      setDisplayNumber(number);
      return;
    }

    // If the number hasn't actually changed, don't animate at all
    if (number === previousNumberRef.current) {
      return;
    }

    // Update the previous number reference
    previousNumberRef.current = number;

    // Compare old and new digits to find which ones changed
    const oldFormatted = formatted
      ? formatNumber(displayNumberRef.current)
      : displayNumberRef.current.toString();
    const newFormatted = formatted ? formatNumber(number) : number.toString();
    const oldDigits = oldFormatted.split("");
    const newDigits = newFormatted.split("");

    // Find the indices that need to be animated and determine direction
    const changedIndices = new Set<number>();
    const isIncreasing = number > displayNumberRef.current;

    // Check each position for changes
    const maxLength = Math.max(oldDigits.length, newDigits.length);
    let leftmostChangedIndex = -1;

    for (let i = 0; i < maxLength; i++) {
      const oldDigit = oldDigits[i] || "";
      const newDigit = newDigits[i] || "";
      if (oldDigit !== newDigit) {
        changedIndices.add(i);
        // Track the leftmost changed digit
        if (leftmostChangedIndex === -1) {
          leftmostChangedIndex = i;
        }
      }
    }

    // If we found a changed digit, animate all digits to its right as well
    // This mimics how real counters work - when a digit rolls over, all digits to the right animate
    if (leftmostChangedIndex !== -1) {
      for (let i = leftmostChangedIndex + 1; i < maxLength; i++) {
        changedIndices.add(i);
      }
    }

    // If no digits changed, no need to animate
    if (changedIndices.size === 0) {
      displayNumberRef.current = number;
      setDisplayNumber(number);
      return;
    }

    // Start exit animation for changed digits only
    isAnimatingRef.current = true;
    setIsAnimating(true);
    const spans = containerRef.current.querySelectorAll(
      `.${styles.animatedNumber}`,
    );

    const exitAnimationClass = isIncreasing
      ? styles.exitAnimation
      : styles.exitAnimationDown;

    // Apply exit animation to old digits
    spans.forEach((span, index) => {
      if (changedIndices.has(index)) {
        const element = span as HTMLElement;
        clearAnimationClasses(element);
        // Force reflow
        void element.offsetHeight;
        // Add appropriate exit animation with reverse delay (rightmost first)
        const reverseIndex = newDigits.length - 1 - index;
        element.className += ` ${exitAnimationClass}`;
        element.style.animationDelay = `${reverseIndex * 0.05}s`;
      }
    });

    // After exit animation completes, update the number and start enter animation
    setTimeout(() => {
      displayNumberRef.current = number;
      setDisplayNumber(number);

      // Start enter animation for changed digits only
      setTimeout(() => {
        if (containerRef.current) {
          const newSpans = containerRef.current.querySelectorAll(
            `.${styles.animatedNumber}`,
          );
          const enterAnimationClass = isIncreasing
            ? styles.enterAnimation
            : styles.enterAnimationDownFromTop;

          // Apply enter animation to new digits
          newSpans.forEach((span, index) => {
            if (changedIndices.has(index)) {
              const element = span as HTMLElement;
              clearAnimationClasses(element);
              // Force reflow
              void element.offsetHeight;
              // Add appropriate enter animation with reverse delay (rightmost first)
              const reverseIndex = newDigits.length - 1 - index;
              element.className += ` ${enterAnimationClass}`;
              element.style.animationDelay = `${reverseIndex * 0.05}s`;
            }
          });
        }

        // Reset animation state after enter animation completes
        // Add extra time to account for cascading delays (max 0.05s * number of digits)
        const maxDelay = Math.max(0, (newDigits.length - 1) * 0.05);
        setTimeout(
          () => {
            isAnimatingRef.current = false;
            setIsAnimating(false);
          },
          150 + maxDelay * 1000,
        ); // Match enter animation duration + cascading delays
      }, 50); // Small delay to ensure DOM has updated with new digits
    }, 150); // Match exit animation duration
  }, [number, formatted, prefersReducedMotion]);

  useEffect(() => {
    animateNumber();
  }, [animateNumber, prefersReducedMotion]);

  return { containerRef, displayNumber };
}

interface Props {
  number: number;
  formatted?: boolean;
  /** Accessible label for the number (e.g., "Comment count", "View count") */
  ariaLabel?: string;
  /** Whether to announce changes to screen readers */
  announceChanges?: boolean;
}

function formatNumber(num: number): string {
  if (num < 1000) {
    return num.toString();
  }

  if (num < 10000) {
    // Format like 1.2k, 3.4k, etc
    const thousands = Math.floor(num / 100) / 10;
    return `${thousands}k`;
  }

  // For numbers 10000 and above, round down to nearest thousand
  // Comment counts above 100k is a champagne problem
  const roundedThousands = Math.floor(num / 1000);
  return `${roundedThousands}k`;
}

/**
 * AnimatedNumber component that displays a number with each digit sliding in separately
 * for a dynamic counting effect.
 *
 * @accessibility
 * - Supports screen readers with ARIA live regions
 * - Respects prefers-reduced-motion for users with motion sensitivity
 * - Provides proper semantic markup and labels
 */
function AnimatedNumber({
  number,
  formatted = false,
  ariaLabel,
  announceChanges = true,
}: Props) {
  const { containerRef, displayNumber } = useAnimatedNumber(number, formatted);

  const formattedDisplayNumber = formatted
    ? formatNumber(displayNumber)
    : displayNumber.toString();
  const displayDigits = formattedDisplayNumber.split("");

  return (
    <span
      ref={containerRef}
      className={styles.numberContainer}
      role="img"
      aria-label={ariaLabel}
      aria-live={announceChanges ? "polite" : "off"}
      aria-atomic="true"
      tabIndex={-1}
    >
      {displayDigits.map((digit, index) => (
        <span
          key={index}
          className={styles.animatedNumber}
          style={{
            animationDelay: `${index * 0.1}s`,
          }}
          aria-hidden="true"
        >
          {digit}
        </span>
      ))}
      {/* Screen reader accessible version - always shows the actual current number */}
      <span className={styles.srOnly} aria-live="polite">
        {formatted ? formatNumber(number) : number.toString()}
      </span>
    </span>
  );
}

export default AnimatedNumber;
export { formatNumber };
